import React, { useEffect, useState } from "react";
import { Input } from "../ui";
import { Button } from "../ui";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui";
import axios from "axios";
import ImageUpload from "./imageUpload";
import { useProductForm } from "../hooks/useProductForm";
import LoadingSpinner from "../common/LoadingSpinner";
import { X } from "lucide-react";
import config from "../../config/config";

const ProductForm = ({ editingProduct, setEditingProduct, onSuccess }) => {
  const { productData, setProductData, loading, handleSubmit } =
    useProductForm(editingProduct);
  const [newAddOn, setNewAddOn] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${config.apiUrl}/api/categories/list`);
      setCategories(
        Array.isArray(response.data) ? response.data : response.data.data || []
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (editingProduct) {
      const formatPrice = (price) => {
        if (typeof price === "number") return price.toString();
        if (typeof price === "string") return price.replace(/[^0-9.]/g, "");
        return "";
      };

      setProductData({
        name: editingProduct.name || "",
        image: editingProduct.image || null,
        category: editingProduct.category || "",
        categoryId: editingProduct.categoryId || "",
        description: editingProduct.description || "",
        price: formatPrice(editingProduct.price),
        addOn: editingProduct.addOn || [],
      });
    }
  }, [editingProduct]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProductData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    const success = await handleSubmit(e);
    if (success && onSuccess) {
      onSuccess();
    }
  };

  const handleAddAddOn = () => {
    if (newAddOn.trim()) {
      setProductData((prev) => ({
        ...prev,
        addOn: [...(prev.addOn || []), newAddOn.trim()],
      }));
      setNewAddOn("");
    }
  };

  const handleRemoveAddOn = (index) => {
    setProductData((prev) => ({
      ...prev,
      addOn: prev.addOn.filter((_, i) => i !== index),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddAddOn();
    }
  };

  if (loading || loadingCategories) return <LoadingSpinner />;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">
        {editingProduct ? "Modificar Producto" : "Agregar Producto"}
      </h2>
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            Nombre de Producto
          </label>
          <Input
            name="name"
            value={productData.name}
            onChange={handleInputChange}
            placeholder="Escribe aquí"
            className="border-orange-200 focus:border-orange-500 focus:ring-orange-500"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            Imagen de Producto
          </label>
          <ImageUpload
            image={productData.image}
            onImageChange={handleImageUpload}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            Categoría del Producto
          </label>
          <Select
            value={productData.category}
            onValueChange={(value) => {
              const selectedCategory = categories.find(
                (cat) => cat._id === value
              );
              setProductData((prev) => ({
                ...prev,
                category: selectedCategory.name,
                categoryId: value,
              }));
            }}
          >
            <SelectTrigger className="border-orange-200">
              <SelectValue placeholder="Selecciona la categoría">
                {productData.category || "Selecciona la categoría"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category._id} value={category._id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            AddOn
          </label>
          <div className="flex space-x-2">
            <Input
              value={newAddOn}
              onChange={(e) => setNewAddOn(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Agregar AddOn"
              className="border-orange-200 focus:border-orange-500 focus:ring-orange-500"
            />
            <Button
              type="button"
              onClick={handleAddAddOn}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Añadir Elemento
            </Button>
          </div>
          <div className="mt-2 space-y-2">
            {productData.addOn?.map((addon, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-orange-50 rounded-md"
              >
                <span>{addon}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveAddOn(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            Descripción
          </label>
          <textarea
            name="description"
            value={productData.description}
            onChange={handleInputChange}
            placeholder="Escribe aquí la descripción del producto"
            className="w-full px-3 py-2 border border-orange-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            rows={4}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-lg font-medium text-gray-700">
            Precio
          </label>
          <Input
            type="number"
            name="price"
            value={productData.price}
            onChange={handleInputChange}
            placeholder="0"
            className="border-orange-200 focus:border-orange-500 focus:ring-orange-500"
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 rounded-lg transition-colors duration-200"
          disabled={loading}
        >
          {loading ? "Guardando..." : editingProduct ? "Modificar" : "Agregar"}
        </Button>
      </form>
    </div>
  );
};

export default ProductForm;
